/**
 * Browser-side image → WebP conversion using Canvas API.
 *
 * Runs before admin uploads so the API only ever sees WebP, saving
 * ~30% R2 storage vs storing original JPEG/PNG. AVIF inputs pass
 * through (smaller than re-encoded WebP). GIF throws so callers can
 * toast a user-friendly error — canvas can only capture one frame
 * and would corrupt animations.
 *
 * Server-side has its own WASM path in apps/api/src/lib/webp-encode
 * for feed imports — these two paths are independent.
 */

export class GifNotSupportedError extends Error {
  constructor() {
    super('GIF_NOT_SUPPORTED');
    this.name = 'GifNotSupportedError';
  }
}

/**
 * Convert a JPEG/PNG file to WebP. Returns the same file when input
 * is already WebP/AVIF. Throws GifNotSupportedError on GIF.
 *
 * Quality 0.85 is a sweet spot — visually near-identical to JPEG q85
 * while ~30% smaller. Bump higher only if visual complaints surface.
 */
export async function convertImageToWebp(file: File, quality = 0.85): Promise<File> {
  const mime = file.type.toLowerCase();

  if (mime === 'image/webp' || mime === 'image/avif') {
    return file;
  }
  if (mime === 'image/gif') {
    throw new GifNotSupportedError();
  }
  if (mime !== 'image/jpeg' && mime !== 'image/png') {
    // Unknown type — let backend MIME validator handle it.
    return file;
  }

  const blob = await renderToWebpBlob(file, quality);
  const newName = file.name.replace(/\.(jpe?g|png)$/i, '.webp');
  return new File([blob], newName, { type: 'image/webp' });
}

function renderToWebpBlob(file: File, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          URL.revokeObjectURL(url);
          reject(new Error('Canvas 2D context unavailable'));
          return;
        }
        ctx.drawImage(img, 0, 0);
        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(url);
            if (blob) resolve(blob);
            else reject(new Error('canvas.toBlob returned null'));
          },
          'image/webp',
          quality,
        );
      } catch (e) {
        URL.revokeObjectURL(url);
        reject(e instanceof Error ? e : new Error(String(e)));
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Image decode failed'));
    };
    img.src = url;
  });
}
